'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface FooterLink {
  label: string;
  href: string;
}

interface FooterSection {
  title: string;
  links: FooterLink[];
}

const footerSections: FooterSection[] = [
  {
    title: 'Practice Areas',
    links: [
      { label: 'Probate & Estate Litigation', href: '/practice-areas' },
      { label: 'Personal Injury Law', href: '/practice-areas' },
      { label: 'Insurance Law', href: '/practice-areas' },
      { label: 'Contracts & Business', href: '/practice-areas' },
    ],
  },
  {
    title: 'About Us',
    links: [
      { label: 'Our Firm', href: '/about-us' },
      { label: 'Attorney Profile', href: '/about-us' },
      { label: 'Our Values', href: '/about-us' },
      { label: 'Community Involvement', href: '/about-us' },
    ],
  },
  {
    title: 'Resources',
    links: [
      { label: 'Legal Guides', href: '/contact' },
      { label: 'FAQ', href: '/contact' },
      { label: 'Case Studies', href: '/contact' },
      { label: 'Client Portal', href: '/contact' },
    ],
  },
];

const socialLinks = [
  { icon: 'facebook', href: '#', label: 'Facebook' },
  { icon: 'twitter', href: '#', label: 'Twitter' },
  { icon: 'linkedin', href: '#', label: 'LinkedIn' },
  { icon: 'instagram', href: '#', label: 'Instagram' },
];

export default function Footer() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentYear, setCurrentYear] = useState('');

  useEffect(() => {
    setIsHydrated(true);
    setCurrentYear(new Date().getFullYear().toString());
  }, []);

  if (!isHydrated) {
    return (
      <footer className="bg-primary text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div className="space-y-4">
              <div className="h-12 w-48 bg-white/10 rounded animate-pulse" />
              <div className="h-4 w-full bg-white/10 rounded animate-pulse" />
              <div className="h-4 w-3/4 bg-white/10 rounded animate-pulse" />
            </div>
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer className="relative text-white">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <AppImage
          src="/assets/images/footer_legal_office_elegant_backdrop.png"
          alt="Professional legal office environment with elegant navy blue and gold accents"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-primary/98 to-navy-dark/98" />
      </div>
      
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-12 mb-12">
          <div className="lg:col-span-2 space-y-6">
            <Link href="/homepage" className="flex items-center space-x-3 group">
              <div className="relative">
                <div className="absolute inset-0 bg-accent/20 rounded-lg blur-lg group-hover:blur-xl transition-all duration-300" />
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 48 48"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="relative transition-transform duration-300 group-hover:scale-110 drop-shadow-2xl"
                  style={{ filter: 'drop-shadow(0 4px 12px rgba(212, 175, 55, 0.5))' }}
                >
                  <rect width="48" height="48" rx="8" fill="#D4AF37" />
                  <path d="M14 34V14H18V30H28V34H14Z" fill="#1B365D" />
                  <path d="M28 14H34L28 24L34 34H28L22 24L28 14Z" fill="#1B365D" />
                </svg>
              </div>
              <div className="flex flex-col">
                <span className="text-white font-serif font-bold text-2xl leading-tight tracking-tight drop-shadow-lg" style={{ textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8), 0 0 10px rgba(212, 175, 55, 0.3)' }}>
                  Lubbert Associates
                </span>
                <span className="text-accent text-sm font-bold tracking-wider uppercase drop-shadow-md" style={{ textShadow: '1px 1px 3px rgba(0, 0, 0, 0.8)' }}>
                  LLC
                </span>
              </div>
            </Link>

            <p className="text-white leading-relaxed max-w-md font-medium drop-shadow-md" style={{ textShadow: '1px 1px 3px rgba(0, 0, 0, 0.8)' }}>
              Delivering justice with purpose through integrity-driven advocacy and strategic
              legal excellence. Your trusted partner in navigating life's most challenging legal
              moments.
            </p>

            <div className="space-y-3">
              <a
                href="tel:+18078084167"
                className="flex items-center gap-3 text-white hover:text-accent transition-colors duration-300 font-medium drop-shadow-md"
                style={{ textShadow: '1px 1px 3px rgba(0, 0, 0, 0.8)' }}
              >
                <Icon name="PhoneIcon" size={20} variant="outline" />
                <span>+1-807-808-4167</span>
              </a>
              <a
                href="tel:+18078084269"
                className="flex items-center gap-3 text-white hover:text-accent transition-colors duration-300 font-medium drop-shadow-md"
                style={{ textShadow: '1px 1px 3px rgba(0, 0, 0, 0.8)' }}
              >
                <Icon name="PhoneIcon" size={20} variant="outline" />
                <span>Fax: +1-807-808-4269</span>
              </a>
              <a
                href="mailto:zlubbert@lubbertassociatesllc.com"
                className="flex items-center gap-3 text-white hover:text-accent transition-colors duration-300 font-medium drop-shadow-md"
                style={{ textShadow: '1px 1px 3px rgba(0, 0, 0, 0.8)' }}
              >
                <Icon name="EnvelopeIcon" size={20} variant="outline" />
                <span>zlubbert@lubbertassociatesllc.com</span>
              </a>
              <a
                href="mailto:admin@lubbertassociatesllc.com"
                className="flex items-center gap-3 text-white hover:text-accent transition-colors duration-300 font-medium drop-shadow-md"
                style={{ textShadow: '1px 1px 3px rgba(0, 0, 0, 0.8)' }}
              >
                <Icon name="EnvelopeIcon" size={20} variant="outline" />
                <span>Contact: admin@lubbertassociatesllc.com</span>
              </a>
              <div className="flex items-start gap-3 text-white font-medium drop-shadow-md" style={{ textShadow: '1px 1px 3px rgba(0, 0, 0, 0.8)' }}>
                <Icon name="MapPinIcon" size={20} variant="outline" className="flex-shrink-0 mt-1" />
                <span>100 Metcalfe St, Unite: 603<br />Ottawa, ON K1P 5M1 CANADA</span>
              </div>
            </div>
          </div>

          {footerSections.map((section, index) => (
            <div key={index}>
              <h3 className="font-serif text-lg font-bold text-white mb-4 drop-shadow-lg" style={{ textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)' }}>{section.title}</h3>
              <ul className="space-y-3">
                {section.links.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    <Link
                      href={link.href}
                      className="text-white hover:text-accent transition-colors duration-300 text-sm font-medium drop-shadow-md"
                      style={{ textShadow: '1px 1px 3px rgba(0, 0, 0, 0.8)' }}
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="border-t border-white/10 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white text-sm font-medium drop-shadow-md" style={{ textShadow: '1px 1px 3px rgba(0, 0, 0, 0.8)' }}>
              ©2019 Lubbert Associates LLC.
            </p>

            <div className="flex items-center gap-4">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  aria-label={social.label}
                  className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-accent hover:scale-110 transition-all duration-300 shadow-lg"
                >
                  <span className="text-white text-sm font-bold drop-shadow-md" style={{ textShadow: '1px 1px 2px rgba(0, 0, 0, 0.8)' }}>
                    {social.icon.charAt(0).toUpperCase()}
                  </span>
                </a>
              ))}
            </div>
          </div>

          <div className="mt-6 p-4 bg-white/5 rounded-lg border border-white/10">
            <p className="text-white text-xs leading-relaxed text-center font-medium drop-shadow-md" style={{ textShadow: '1px 1px 3px rgba(0, 0, 0, 0.8)' }}>
              <strong className="drop-shadow-lg" style={{ textShadow: '1px 1px 3px rgba(0, 0, 0, 0.9)' }}>Attorney Advertising Disclaimer:</strong> The information on this website is
              for general information purposes only. Nothing on this site should be taken as legal
              advice for any individual case or situation. This information is not intended to
              create, and receipt or viewing does not constitute, an attorney-client relationship.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}